import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { X, Download, Info } from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white";
const label = "block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5";

const COMPANIES = [
  { key: "misas", label: "Mis-aş Gıda İnş. San. Ve Tic. A.Ş" },
  { key: "mis_holding", label: "Mis Holding A.Ş." },
  { key: "koop", label: "S.S. Mis Tüketim Kooperatifi" },
];

function formatIban(v) {
  const raw = (v || "").replace(/\s+/g, "").toUpperCase();
  return raw.replace(/(.{4})/g, "$1 ").trim();
}

export default function DilekceDialog({ open, onClose, member, defaultCompany = null, onCreated }) {
  const [company, setCompany] = useState(defaultCompany);
  const [hisse, setHisse] = useState("");
  const [iban, setIban] = useState("");
  const [busy, setBusy] = useState(false);
  const [dateStr, setDateStr] = useState(() => new Date().toLocaleDateString("tr-TR"));

  const available = COMPANIES.filter(c => Number(member?.[`${c.key}_hisse`] || 0) > 0);

  useEffect(() => {
    if (open) {
      const first = defaultCompany && available.find(c => c.key === defaultCompany?.key || c.key === defaultCompany);
      setCompany(first || available[0] || null);
      setHisse(""); setIban("");
      setDateStr(new Date().toLocaleDateString("tr-TR"));
    }
    // eslint-disable-next-line
  }, [open, defaultCompany]);

  if (!open) return null;

  const maxHisse = company ? Number(member?.[`${company.key}_hisse`] || 0) : 0;

  const submit = async () => {
    if (!company) { toast.error("Şirket seçin"); return; }
    const n = Number(hisse);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Geçerli hisse adedi girin"); return; }
    if (n > maxHisse) { toast.error(`En fazla ${maxHisse} hisse için dilekçe verilebilir`); return; }
    const cleanIban = (iban || "").replace(/\s+/g, "").toUpperCase();
    if (!cleanIban.startsWith("TR") || cleanIban.length !== 26) {
      toast.error("Geçerli bir Türkiye IBAN'ı girin (TR ile başlayan 26 karakter)");
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem("member_token");
      const r = await axios.post(`${API}/member/dilekce`, {
        company: company.key,
        hisse: n,
        iban: cleanIban,
        date_str: dateStr,
      }, {
        responseType: "blob",
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "dilekce.docx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Dilekçeniz oluşturuldu ve yönetime iletildi");
      onCreated?.();
      onClose();
    } catch (err) {
      let msg = "Dilekçe oluşturulamadı";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = j.detail || msg;
        } catch (_) { /* ignore */ }
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="dilekce-dialog">
      <div className="crisp-card p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Hisse Satış / Ayrılış Dilekçesi</h3>
            <p className="text-xs text-slate-500 mt-0.5">Aşağıdaki dilekçe Yönetim Kurulu Başkanlığına iletilmek üzere sizin adınıza otomatik olarak oluşturulur.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="dilekce-close"><X size={18}/></button>
        </div>

        {available.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
            Aktif hisseniz bulunmadığı için dilekçe oluşturulamıyor.
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <span className={label}>Şirket</span>
              <div className="flex flex-wrap gap-2">
                {available.map(c => (
                  <button key={c.key} type="button" onClick={() => setCompany(c)}
                    data-testid={`dilekce-co-${c.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${company?.key === c.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    {c.label} · {member[`${c.key}_hisse`]} hisse
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={label}>Satılacak Hisse</span>
                <input data-testid="dilekce-hisse" type="number" min="1" max={maxHisse} className={inputCls}
                       value={hisse} onChange={e => setHisse(e.target.value)} placeholder={`En fazla ${maxHisse}`} />
              </div>
              <div>
                <span className={label}>Tarih</span>
                <input data-testid="dilekce-date" className={inputCls} value={dateStr}
                       onChange={e => setDateStr(e.target.value)} placeholder="gg.aa.yyyy" />
              </div>
            </div>

            <div>
              <span className={label}>IBAN <span className="text-slate-400 font-normal normal-case tracking-normal">(Yalnızca kendi adınıza olan IBAN olması gerekmektedir.)</span></span>
              <input data-testid="dilekce-iban" className={inputCls + " font-mono-nums tracking-wider uppercase"}
                     value={iban}
                     onChange={e => setIban(formatIban(e.target.value))}
                     placeholder="TR__ ____ ____ ____ ____ ____ __" maxLength={32} />
              <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1"><Info size={11}/> IBAN, satış bedelinin gönderileceği kendi banka hesap numaranızdır.</div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[11px] text-slate-600">
              Dilekçeniz gönderildikten sonra Word (DOCX) dosyası olarak da indirilir. Bir kopyası yönetim tarafında görüntülenir ve gerekli işlemler başlatılır.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="dilekce-cancel">İptal</button>
              <button onClick={submit} disabled={busy} data-testid="dilekce-submit"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
                <Download size={14}/> {busy ? "Hazırlanıyor..." : "Dilekçeyi Oluştur ve İndir"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
